import { useEffect, useRef, useCallback } from 'react';

// Tick subscriber callback type
export type TickCallback = (tickCount: number) => void;

interface TickSubscription {
  id: number;
  callback: TickCallback;
  interval: number;
  lastTick: number;
}

type RafHandle = number | ReturnType<typeof setTimeout>;

type StepAndPauseFn = () => StepAndPauseFn;

interface RafProvider {
  requestAnimationFrame: (callback: FrameRequestCallback) => RafHandle;
  cancelAnimationFrame: (handle: RafHandle) => void;
  now: () => number;
}

const TICK_RATE = 60;
const TICK_INTERVAL_MS = 1000 / TICK_RATE;

const createDefaultRafProvider = (): RafProvider => {
  const now = () => globalThis.performance?.now?.() ?? Date.now();

  const requestAnimationFrameFn =
    typeof globalThis.requestAnimationFrame === 'function'
      ? (callback: FrameRequestCallback): RafHandle =>
          globalThis.requestAnimationFrame(callback)
      : (callback: FrameRequestCallback): RafHandle =>
          setTimeout(() => callback(now()), TICK_INTERVAL_MS);

  const cancelAnimationFrameFn =
    typeof globalThis.cancelAnimationFrame === 'function'
      ? (handle: RafHandle): void =>
          globalThis.cancelAnimationFrame(handle as number)
      : (handle: RafHandle): void =>
          clearTimeout(handle as ReturnType<typeof setTimeout>);

  return {
    requestAnimationFrame: requestAnimationFrameFn,
    cancelAnimationFrame: cancelAnimationFrameFn,
    now,
  };
};

export class TickSystem {
  private tickCount = 0;
  private subscriptions: TickSubscription[] = [];
  private nextSubscriptionId = 1;
  private rafHandle: RafHandle | null = null;
  private subscriberCount = 0;
  private lastFrameTime = 0;
  private testingMode = false;
  private debugStepMode = false;
  private externallyPaused = false;

  constructor(
    private readonly rafProvider: RafProvider = createDefaultRafProvider()
  ) {}

  private executeTick = (): void => {
    this.tickCount += 1;

    this.subscriptions.forEach(subscription => {
      const ticksSinceLastRun = this.tickCount - subscription.lastTick;
      if (ticksSinceLastRun >= subscription.interval) {
        subscription.callback(this.tickCount);
        subscription.lastTick = this.tickCount;
      }
    });
  };

  private masterTick = (currentTime: number): void => {
    if (this.testingMode || this.debugStepMode || this.externallyPaused) {
      this.rafHandle = null;
      return;
    }

    const deltaTime = currentTime - this.lastFrameTime;

    if (deltaTime >= TICK_INTERVAL_MS) {
      this.lastFrameTime = currentTime - (deltaTime % TICK_INTERVAL_MS);
      this.executeTick();
    }

    if (this.shouldRunMasterTick()) {
      this.rafHandle = this.rafProvider.requestAnimationFrame(this.masterTick);
    } else {
      this.rafHandle = null;
    }
  };

  private shouldRunMasterTick(): boolean {
    return (
      this.subscriberCount > 0 &&
      !this.testingMode &&
      !this.debugStepMode &&
      !this.externallyPaused
    );
  }

  private cancelScheduledFrame(): void {
    if (this.rafHandle !== null) {
      this.rafProvider.cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }

  private startMasterTick = (): void => {
    this.subscriberCount += 1;
    if (this.rafHandle === null && this.shouldRunMasterTick()) {
      this.lastFrameTime = this.rafProvider.now();
      this.rafHandle = this.rafProvider.requestAnimationFrame(this.masterTick);
    }
  };

  private stopMasterTick = (): void => {
    this.subscriberCount -= 1;
    if (this.subscriberCount <= 0) {
      this.subscriberCount = 0;
      this.cancelScheduledFrame();
    }
  };

  public subscribe(callback: TickCallback, interval: number = 1): number {
    const subscription: TickSubscription = {
      id: this.nextSubscriptionId++,
      callback,
      interval,
      lastTick: Math.max(0, this.tickCount - interval),
    };

    this.subscriptions.push(subscription);
    this.startMasterTick();

    return subscription.id;
  }

  public unsubscribe(subscriptionId: number): void {
    const nextSubscriptions = this.subscriptions.filter(
      sub => sub.id !== subscriptionId
    );
    if (nextSubscriptions.length !== this.subscriptions.length) {
      this.subscriptions = nextSubscriptions;
      this.stopMasterTick();
    }
  }

  public getCurrentTick(): number {
    return this.tickCount;
  }

  public resetTickCount(): void {
    this.tickCount = 0;
    this.subscriptions.forEach(sub => {
      sub.lastTick = 0;
    });
  }

  public enableTestingMode(): void {
    this.testingMode = true;
    this.cancelScheduledFrame();
  }

  public disableTestingMode(): void {
    this.testingMode = false;
    if (this.rafHandle === null && this.shouldRunMasterTick()) {
      this.lastFrameTime = this.rafProvider.now();
      this.rafHandle = this.rafProvider.requestAnimationFrame(this.masterTick);
    }
  }

  public advanceTicks(count: number = 1): void {
    if (!this.testingMode) {
      throw new Error(
        'advanceTicks can only be used in testing mode. Call enableTestingMode() first.'
      );
    }

    for (let i = 0; i < count; i += 1) {
      this.executeTick();
    }
  }

  public getTickSubscriptionCount(): number {
    return this.subscriptions.length;
  }

  public isTickSystemRunning(): boolean {
    return this.rafHandle !== null;
  }

  public isInTestingMode(): boolean {
    return this.testingMode;
  }

  public enableDebugStepMode(): void {
    this.debugStepMode = true;
    this.cancelScheduledFrame();
  }

  public disableDebugStepMode(): void {
    this.debugStepMode = false;
    if (this.rafHandle === null && this.shouldRunMasterTick()) {
      this.lastFrameTime = this.rafProvider.now();
      this.rafHandle = this.rafProvider.requestAnimationFrame(this.masterTick);
    }
  }

  public stepAndPause(): StepAndPauseFn {
    if (!this.debugStepMode) {
      this.enableDebugStepMode();
    }

    this.executeTick();
    return () => this.stepAndPause();
  }

  public isInDebugStepMode(): boolean {
    return this.debugStepMode;
  }

  public setExternallyPaused(paused: boolean): void {
    if (this.externallyPaused === paused) {
      return;
    }

    this.externallyPaused = paused;

    if (paused) {
      this.cancelScheduledFrame();
      return;
    }

    if (this.rafHandle === null && this.shouldRunMasterTick()) {
      this.lastFrameTime = this.rafProvider.now();
      this.rafHandle = this.rafProvider.requestAnimationFrame(this.masterTick);
    }
  }

  public isExternallyPaused(): boolean {
    return this.externallyPaused;
  }

  public waitForTicks(tickCount: number): Promise<number> {
    if (this.testingMode) {
      throw new Error(
        'waitForTicks should not be used in testing mode. Use advanceTicks() instead.'
      );
    }

    return new Promise(resolve => {
      const targetTick = this.tickCount + tickCount;

      const checkTick = (): void => {
        if (this.tickCount >= targetTick) {
          resolve(this.tickCount);
        } else {
          this.rafProvider.requestAnimationFrame(() => checkTick());
        }
      };

      this.rafProvider.requestAnimationFrame(() => checkTick());
    });
  }

  public waitForNextTick(): Promise<number> {
    return this.waitForTicks(1);
  }
}

const sharedTickSystem = new TickSystem();

export const getSharedTickSystem = (): TickSystem => sharedTickSystem;

export const getCurrentTick = (): number => sharedTickSystem.getCurrentTick();

export const resetTickCount = (): void => {
  sharedTickSystem.resetTickCount();
};

export const enableTestingMode = (): void => {
  sharedTickSystem.enableTestingMode();
};

export const disableTestingMode = (): void => {
  sharedTickSystem.disableTestingMode();
};

export const advanceGameTick = (count: number = 1): void => {
  sharedTickSystem.advanceTicks(count);
};

export const getTickSubscriptionCount = (): number =>
  sharedTickSystem.getTickSubscriptionCount();

export const isTickSystemRunning = (): boolean =>
  sharedTickSystem.isTickSystemRunning();

export const isInTestingMode = (): boolean =>
  sharedTickSystem.isInTestingMode();

export const enableDebugStepMode = (): void => {
  sharedTickSystem.enableDebugStepMode();
};

export const disableDebugStepMode = (): void => {
  sharedTickSystem.disableDebugStepMode();
};

export const stepAndPause = () => sharedTickSystem.stepAndPause();

export const isInDebugStepMode = (): boolean =>
  sharedTickSystem.isInDebugStepMode();

export const waitForTicks = (tickCount: number): Promise<number> =>
  sharedTickSystem.waitForTicks(tickCount);

export const waitForNextTick = (): Promise<number> =>
  sharedTickSystem.waitForNextTick();

export const setTickSystemPaused = (paused: boolean): void => {
  sharedTickSystem.setExternallyPaused(paused);
};

export const isTickSystemPaused = (): boolean =>
  sharedTickSystem.isExternallyPaused();

// Hook for subscribing to game ticks
export const useGameTick = (
  callback: TickCallback,
  interval: number = 1,
  enabled: boolean = true
): {
  currentTick: number;
  subscribe: (cb: TickCallback, interval?: number) => number;
  unsubscribe: (id: number) => void;
} => {
  const tickSystem = sharedTickSystem;
  const subscriptionIdRef = useRef<number | null>(null);
  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  const subscribeToTick = useCallback(
    (cb: TickCallback, tickInterval: number = 1): number => {
      return tickSystem.subscribe(cb, tickInterval);
    },
    [tickSystem]
  );

  const unsubscribeFromTick = useCallback(
    (id: number): void => {
      tickSystem.unsubscribe(id);
    },
    [tickSystem]
  );

  useEffect(() => {
    if (enabled) {
      subscriptionIdRef.current = subscribeToTick(
        callbackRef.current,
        interval
      );

      return () => {
        if (subscriptionIdRef.current !== null) {
          unsubscribeFromTick(subscriptionIdRef.current);
          subscriptionIdRef.current = null;
        }
      };
    }

    return undefined;
  }, [enabled, interval, subscribeToTick, unsubscribeFromTick]);

  return {
    currentTick: tickSystem.getCurrentTick(),
    subscribe: subscribeToTick,
    unsubscribe: unsubscribeFromTick,
  };
};

export const useTickSubscription = (): {
  subscribe: (callback: TickCallback, interval?: number) => number;
  unsubscribe: (id: number) => void;
  currentTick: number;
} => {
  const tickSystem = sharedTickSystem;

  const subscribeToTick = useCallback(
    (callback: TickCallback, interval: number = 1): number => {
      return tickSystem.subscribe(callback, interval);
    },
    [tickSystem]
  );

  const unsubscribeFromTick = useCallback(
    (id: number): void => {
      tickSystem.unsubscribe(id);
    },
    [tickSystem]
  );

  return {
    subscribe: subscribeToTick,
    unsubscribe: unsubscribeFromTick,
    currentTick: tickSystem.getCurrentTick(),
  };
};
